import { useCallback, useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type RelayModernEnvironment from 'relay-runtime/lib/store/RelayModernEnvironment';

import FeatureGates from '@atlaskit/feature-gate-js-client';

import {
	type OperationalEvent,
	useOperationalAnalytics,
} from '../analytics/use-operational-analytics';
import { useUIAnalytics } from '../analytics/use-ui-analytics';
import { getProductForTranslation } from '../services/get-product-translation-key';
import { type Edition, type Flag, type LastKnownTrial, type Product } from '../types';

import {
	type ConfluenceEdition,
	type useOnAttemptToUpgradeQuery,
} from './__generated__/useOnAttemptToUpgradeQuery.graphql';
import { isProductOneOf } from './is-product-one-of';
import { getHamsKey } from './product-to-hams-key';

// Equates to ~4 minutes of attempts spread out between the delay of waiting for the response
export const MAX_ATTEMPTS = 48;
const TIME_BETWEEN_ATTEMPTS_MS = 5_000; // 5 seconds

type UpgradeState = {
	customerHasUpgraded: boolean;
	productHasUpgraded: boolean;
	upgradedEdition?: Edition | null;
};

type TrialAndEditionInformation = {
	confluenceEdition?: ConfluenceEdition | null;
	lastKnownTrial?: LastKnownTrial | null;
};

async function getHasJiraUpgraded(product: Product): Promise<UpgradeState> {
	// TODO: Add the query for GUPs to find out if the customer is on a manual trial
	const response = await fetch('/rest/internal/latest/meta/edition');

	if (!response.ok) {
		throw new Error(`Fetching upgrade status failed with ${response.status}`);
	}

	const data = (await response.json()) as Partial<Record<string, Edition | unknown>>;

	const key = getHamsKey(product);

	const productHasUpgraded = data[key] === 'standard' || data[key] === 'premium';

	return {
		productHasUpgraded,
		customerHasUpgraded: false,
	};
}

const editionMap: Partial<Record<ConfluenceEdition, Edition>> = {
	STANDARD: 'standard',
	PREMIUM: 'premium',
};

const offeringKeyToEditionMap: Partial<Record<string, Edition>> = {
	'7547cfd6-a804-4e42-8bed-e656d49ea09e': 'free',
	'2eda1119-fab4-4699-ab37-ff8f12052cde': 'standard',
	'06ef245c-5fa3-473b-8822-5e6fe072ae4b': 'premium',
};

async function getConfluenceTrialAndEditionInformation(
	environment: RelayModernEnvironment,
	entitlementId: string,
): Promise<TrialAndEditionInformation> {
	const response = await fetchQuery<useOnAttemptToUpgradeQuery>(
		environment,
		graphql`
			query useOnAttemptToUpgradeQuery($entitlementId: ID!) {
				confluence_tenantContext {
					editions {
						confluence
					}
				}
				growthUnifiedProfile_getEntitlementProfile(entitlementId: $entitlementId) {
					lastKnownTrial {
						trialEndTimeStamp
						trialType
						offeringId
						createdAt
					}
				}
			}
		`,
		{
			entitlementId,
		},
	).toPromise();

	return {
		confluenceEdition: response?.confluence_tenantContext?.editions?.confluence,
		lastKnownTrial: response?.growthUnifiedProfile_getEntitlementProfile?.lastKnownTrial,
	};
}

async function getHasConfluenceUpgraded({
	environment,
	entitlementId,
}: {
	entitlementId: string;
	environment: RelayModernEnvironment;
}): Promise<UpgradeState> {
	const { confluenceEdition, lastKnownTrial } = await getConfluenceTrialAndEditionInformation(
		environment,
		entitlementId,
	);

	const upgradedEdition: Edition | undefined = confluenceEdition
		? editionMap?.[confluenceEdition]
		: undefined;

	const gupsEdition = lastKnownTrial?.offeringId
		? offeringKeyToEditionMap[lastKnownTrial?.offeringId]
		: undefined;

	const customerHasUpgraded = hasTrialManuallyStarted(lastKnownTrial);

	// Ten minutes ago in milliseconds since epoch
	const tenMinutesAgo = Date.now() - 10 * 60 * 1000;

	// if GUPS entitlement profile is older than 10 minutes, then return false as we cannot infer that they have upgraded
	if (!lastKnownTrial?.createdAt || lastKnownTrial?.createdAt < tenMinutesAgo) {
		return {
			productHasUpgraded: false,
			customerHasUpgraded,
			upgradedEdition,
		};
	}

	return {
		productHasUpgraded: Boolean(upgradedEdition && gupsEdition && upgradedEdition === gupsEdition),
		customerHasUpgraded,
		upgradedEdition,
	};
}

function hasTrialManuallyStarted(lastTrialInfo: LastKnownTrial | null | undefined) {
	const trialEndTimeStamp = lastTrialInfo?.trialEndTimeStamp;
	const trialType = lastTrialInfo?.trialType;

	return Boolean(
		trialEndTimeStamp && trialEndTimeStamp >= Date.now() && trialType === 'MANUAL_TRIAL',
	);
}

async function getHasProductUpgraded({
	product,
	environment,
	entitlementId,
}: {
	entitlementId: string;
	environment: RelayModernEnvironment;
	product: Product;
}): Promise<UpgradeState> {
	if (product === 'confluence') {
		return getHasConfluenceUpgraded({
			environment,
			entitlementId,
		});
	}

	if (isProductOneOf(product, ['jira', 'jira-product-discovery', 'jira-service-management'])) {
		return getHasJiraUpgraded(product);
	}

	throw new Error(`Unsupported product ${product}`);
}

async function linearRetry<T>(
	fn: () => Promise<T>,
	sendOperationalEvent: (event: OperationalEvent) => void,
) {
	const sendAnalytics = (action: string, additionalAttributes: Record<string, unknown> = {}) => {
		sendOperationalEvent({
			action,
			actionSubject: 'pollForUpgrade',
			actionSubjectId: 'editionAwareness',
			attributes: {
				...additionalAttributes,
			},
		});
	};

	const waitForInterval = () =>
		new Promise((resolve) => setTimeout(resolve, TIME_BETWEEN_ATTEMPTS_MS));

	sendAnalytics('started');

	for (let attempts = 0; attempts < MAX_ATTEMPTS; attempts++) {
		try {
			const result = await fn();

			if (result) {
				sendAnalytics('success', { attempt: attempts + 1 });
				return true;
			}

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		} catch (error) {
			sendAnalytics('error', {
				attempt: attempts + 1,
				error: error instanceof Error ? error.message : 'Unknown error',
			});

			if (attempts < MAX_ATTEMPTS - 1) {
				await waitForInterval();
			}
		}
	}

	sendAnalytics('timeout', { attempts: MAX_ATTEMPTS });

	return false;
}

export function useOnAttemptToUpgrade({
	product,
	edition,
	isInCollection,
	showFlag,
	hideFlag,
	entitlementId,
	lastKnownTrial,
}: {
	edition: Edition;
	entitlementId: string;
	hideFlag?: (id: string) => void;
	isInCollection?: boolean;
	lastKnownTrial?: LastKnownTrial | undefined;
	product: Product;
	showFlag?: (flag: Flag) => void;
}): {
	startCheckingForAttemptToUpgrade: () => Promise<boolean>;
} {
	const sendOperationalEvent = useOperationalAnalytics({ product, edition });
	const sendUIEvent = useUIAnalytics({ product, edition });
	const intl = useIntl();

	const environment = useRelayEnvironment();

	const showUpgradeInProgressFlag = useCallback(
		function () {
			if (product !== 'confluence' || isInCollection) {
				// Currently we're only targeting confluence upgrades
				return;
			}

			const cohort = FeatureGates.getExperimentValue<string>(
				'manual_trial_w_pde_onboarding',
				'cohort',
				'not-enrolled',
			);

			sendUIEvent({
				action: 'show',
				actionSubject: 'editionAwarenessUpgradePolling',
				actionSubjectId: 'upgradeInProgressFlag',
				attributes: {
					trackEventGrowthInitiative: 'transmuters',
				},
			});

			if (cohort === 'variation') {
				showFlag?.({
					id: '@atlassian/edition-awareness.upgrade-in-progress-flag',
					title: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-in-progress-flag.title',
						defaultMessage: 'Upgrading your plan',
						description: 'Flag title for informing the customer they are being upgraded',
					}),
					description: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-in-progress-flag.description',
						defaultMessage:
							'This may take a couple of minutes. We’ll let you know when it’s finished.',
						description: 'Upgrade in progress message',
					}),
				});
			}
		},
		[product, sendUIEvent, showFlag, intl, isInCollection],
	);

	const showUpgradedFlag = useCallback(
		function (edition?: Edition | null) {
			if (product !== 'confluence' || isInCollection) {
				// Currently we're only targeting confluence upgrades
				return;
			}

			const cohort = FeatureGates.getExperimentValue<string>(
				'manual_trial_w_pde_onboarding',
				'cohort',
				'not-enrolled',
			);

			sendUIEvent({
				action: 'show',
				actionSubject: 'editionAwarenessUpgradePolling',
				actionSubjectId: 'upgradeCompleteFlag',
				attributes: {
					trackEventGrowthInitiative: 'transmuters',
				},
			});

			if (cohort === 'variation') {
				hideFlag?.('@atlassian/edition-awareness.upgrade-in-progress-flag');

				showFlag?.({
					id: '@atlassian/edition-awareness.upgrade-complete-flag',
					title: intl.formatMessage({
						id: '@atlassian/edition-awareness.upgrade-complete-flag.title',
						defaultMessage: 'Refresh to start using new features',
						description:
							'Flag title for informing the customer they need to refresh the page to be on the new edition',
					}),

					description: intl.formatMessage(
						{
							id: '@atlassian/edition-awareness.upgrade-complete-flag.description2',
							defaultMessage:
								'Your upgrade to {product, select, confluence {Confluence} jira {Jira} jpd {Jira Product Discovery} jsm {Jira Service Management} other {}} {edition, select, standard {Standard} premium {Premium} other {}} is complete.',
							description: 'Upgrade complete message',
						},
						{ edition, product: getProductForTranslation(product) },
					),
					actions: [
						{
							onClick: () => {
								window.location.reload();
							},
							content: intl.formatMessage({
								id: '@atlassian/edition-awareness.upgrade-complete-flag.action',
								defaultMessage: 'Refresh',
								description: 'Call to action to refresh the page',
							}),
						},
					],
				});
			}
		},
		[product, sendUIEvent, showFlag, hideFlag, intl, isInCollection],
	);

	const hasRunOnPageLoad = useRef(false);

	// Five minutes ago in milliseconds since epoch
	const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
	const lastKnowTrialCreatedAt = lastKnownTrial?.createdAt || 0;

	const gupsEdition = lastKnownTrial?.offeringId
		? offeringKeyToEditionMap[lastKnownTrial?.offeringId]
		: undefined;

	// check if the FG is on and the edition is different from the edition in GUPS profile and
	// that the GUPS profile was created less than 5 minutes ago and it says that the trial has manually started
	// then we need to poll for the latest edition from TCS
	const shouldCheckOnPageLoad =
		!isInCollection &&
		gupsEdition &&
		edition !== gupsEdition &&
		lastKnowTrialCreatedAt > fiveMinutesAgo &&
		hasTrialManuallyStarted(lastKnownTrial);

	useEffect(() => {
		if (shouldCheckOnPageLoad) {
			// check that GUPS is updated
			linearRetry(async () => {
				const { productHasUpgraded, upgradedEdition } = await getHasProductUpgraded({
					product,
					environment,
					entitlementId,
				});

				if (productHasUpgraded) {
					showUpgradedFlag(upgradedEdition);
				}

				return productHasUpgraded;
			}, sendOperationalEvent);
		}

		hasRunOnPageLoad.current = true;
	}, [
		hasRunOnPageLoad,
		shouldCheckOnPageLoad,
		entitlementId,
		environment,
		product,
		sendOperationalEvent,
		showUpgradedFlag,
	]);

	const startCheckingForAttemptToUpgrade = useCallback(async (): Promise<boolean> => {
		let hasSeenInProgressFlag = false;

		return linearRetry(async () => {
			const { productHasUpgraded, customerHasUpgraded, upgradedEdition } =
				await getHasProductUpgraded({
					product,
					environment,
					entitlementId,
				});

			if (!productHasUpgraded && customerHasUpgraded && !hasSeenInProgressFlag) {
				// Customer has upgraded, but product does not reflect the change
				hasSeenInProgressFlag = true;

				showUpgradeInProgressFlag();
				return false;
			}

			if (productHasUpgraded) {
				showUpgradedFlag(upgradedEdition);
			}

			return productHasUpgraded;
		}, sendOperationalEvent);
	}, [
		environment,
		product,
		sendOperationalEvent,
		entitlementId,
		showUpgradedFlag,
		showUpgradeInProgressFlag,
	]);

	// At a later stage there will be a useEffect which checks if the customer has started a manual trial but is not on
	// a trial yet (aka in product doesn't reflect the action they performed). This polling will show a flag too

	return { startCheckingForAttemptToUpgrade };
}
